import { clsx } from 'clsx';
import { Link } from 'react-router';
import { formatPrice } from 'shared/utils';

import routes from '@/assets/constants/routes-no.ts';
import { FaqSection } from '@/components/marketing/faq-section/index.ts';
import { Section } from '@/components/marketing/section/index.ts';
import { SplitHeroSection } from '@/components/marketing/split-hero-section/index.ts';
import { TestimonialsSection } from '@/components/marketing/testimonials-section/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { testimonialsQuery } from '@/lib/api/requests.ts';
import {
  creditCards,
  type CreditCardFeature,
  type CreditCardInfo,
} from '@/lib/credit-cards/index.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { SEO } from '@/lib/seo.tsx';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import type { Route } from './+types/credit-cards';
import heroImage from './hero-image.svg';

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const testimonials = await queryClient.query(testimonialsQuery());

  return { testimonials };
}

const percentFormatter = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export default function CreditCards({ loaderData }: Route.ComponentProps) {
  const { toggleChat } = useBrainSession();

  const applyAction = async (placement: 'hero' | 'card', cardName?: string) => {
    if (placement === 'hero') {
      sendUserPortalEvent('credit_cards_hero_cta_clicked');
    } else if (cardName) {
      sendUserPortalEvent('credit_cards_card_cta_clicked', { card_name: cardName });
    }

    toggleChat({ goal: 'debt_check_credit_card_2026', step: 'skip_start' });
  };

  return (
    <div className="flex w-full flex-col bg-white pb-16 text-v1-background-200">
      <SEO
        title="Søk om kredittkort: én søknad, flere tilbud"
        description="Én søknad gir deg tilbud fra flere kredittkort. Sammenlign fordeler, effektiv rente, årsavgift og rentefri periode, og velg det kortet som passer deg best."
        path={routes.CREDIT_CARDS}
      />
      <article className="flex flex-col gap-8 sm:gap-15 md:mt-15">
        <SplitHeroSection.Root>
          <SplitHeroSection.Content>
            <div className="mb-4 rounded-full bg-v1-primary-100 px-4 py-1 typography-v1-caption1 font-bold text-white">
              Nyhet
            </div>
            <SplitHeroSection.Title>Søk om kredittkort</SplitHeroSection.Title>

            <p className="mb-6 max-w-md text-center typography-v1-h4 text-balance">
              Hos Scoopr kan du søke og sammenligne kredittkort fra flere banker helt uforpliktende
            </p>

            <SplitHeroSection.List>
              <SplitHeroSection.ListItem>
                Søk om kredittkort hos flere banker
              </SplitHeroSection.ListItem>
              <SplitHeroSection.ListItem>Sammenlign fordeler og bonuser</SplitHeroSection.ListItem>
              <SplitHeroSection.ListItem>Helt uforpliktende</SplitHeroSection.ListItem>
            </SplitHeroSection.List>

            <Button
              color="yellow"
              className="mb-4 w-full max-w-sm md:mb-6 lg:mb-8"
              action={() => applyAction('hero')}
            >
              Logg inn med BankID
            </Button>

            <p className="max-w-md text-center typography-v1-body3">
              Ved å fortsette aksepterer jeg Scoopr sine{' '}
              <Link
                to={routes.TERMS_OF_USE_MIN_SCOOPR}
                className="text-v1-primary-100 hover:underline"
              >
                brukervilkår
              </Link>{' '}
              og{' '}
              <Link to={routes.PRIVACY_STATEMENT} className="text-v1-primary-100 hover:underline">
                personvernerklæring
              </Link>
            </p>
          </SplitHeroSection.Content>

          <SplitHeroSection.ImageContainer>
            <SplitHeroSection.Image
              src={heroImage}
              alt="Kredittkort"
              width={1000}
              height={1021}
              align="center"
            />
          </SplitHeroSection.ImageContainer>
        </SplitHeroSection.Root>

        <CreditCardSection onApply={(cardName) => applyAction('card', cardName)} />

        <BenefitsSection />
        <TestimonialsSection data={loaderData.testimonials} />
        <ChoosingSection />
        <FaqSection items={faqItems} />
      </article>
    </div>
  );
}

const seoLinkClassName = 'font-bold text-v1-primary-100 underline';

const faqItems = [
  {
    title: 'Hvordan funker kredittkort?',
    content: (
      <p>
        Et kredittkort lar deg låne penger fra kortutstederen opp til en bestemt kredittgrense. Du
        får vanligvis en månedlig faktura, og dersom du betaler hele beløpet innen forfallsdato, kan
        du vanligvis unngå renter på varekjøp. Betaler du bare deler av fakturaen, påløper det
        normalt renter på det resterende beløpet.
      </p>
    ),
  },
  {
    title: 'Hvem kan søke kredittkort?',
    content: (
      <p>
        Kravene varierer mellom ulike kredittkort og kortutstedere. Vanlig krav er at du har fylt 18
        år, har fast inntekt og består en kredittvurdering. Enkelte kort kan ha egne krav til alder
        og inntekt.
      </p>
    ),
  },
  {
    title: 'Hvor mye kreditt kan jeg få?',
    content: (
      <p>
        Hvor høy kredittgrense du kan få, avhenger blant annet av inntekt, eksisterende gjeld og den
        samlede økonomiske situasjonen din. Kortutstederen vurderer dette når du søker og bestemmer
        hvilken kredittgrense du eventuelt får.
      </p>
    ),
  },
  {
    title: 'Hvorfor bruke kredittkort?',
    content: (
      <p>
        Et kredittkort kan gi fordeler som bonus, cashback, reiseforsikring og ekstra trygghet ved
        enkelte kjøp. Det kan også gi fleksibilitet ved uforutsette utgifter. For å unngå
        unødvendige rentekostnader er det lurt å betale hele fakturaen innen forfallsdato.
      </p>
    ),
  },
  {
    title: 'Hva koster tjenesten?',
    content: (
      <p>
        Ingenting. Scoopr er en låneformidler med konsesjon fra Finanstilsynet, og vi får betalt av
        banken hvis du takker ja til et tilbud. Det påvirker ikke renten du får.
      </p>
    ),
  },
];

const TextColumns = (props: { children: React.ReactNode }) => {
  return (
    <Section.Container>
      <Section.Content className="flex flex-col gap-8 py-10 typography-v1-body4 md:flex-row md:gap-20">
        {props.children}
      </Section.Content>
    </Section.Container>
  );
};

const TextColumn = (props: { title: string; children: React.ReactNode }) => {
  return (
    <div className="flex flex-1 flex-col gap-4">
      <h2 className="typography-v1-h3">{props.title}</h2>
      {props.children}
    </div>
  );
};

const BenefitsSection = () => {
  return (
    <TextColumns>
      <TextColumn title="Fordeler med kredittkort">
        <p>
          Et kredittkort kan gi deg både fleksibilitet og flere fordeler i hverdagen. Mange
          kredittkort tilbyr bonuspoeng, cashback, reiseforsikring eller rabatter på utvalgte varer
          og tjenester.
        </p>
        <p>
          I tillegg kan kredittkort gi ekstra trygghet ved kjøp på nett og på reise, fordi du
          betaler med kreditten til kortutstederen i stedet for penger direkte fra bankkontoen din.
          Betaler du hele fakturaen innen forfallsdato, kan du som regel unngå renter på varekjøp.
          Fordelene du får, varierer mellom ulike kredittkort, og derfor kan det lønne seg å
          sammenligne kort før du velger.
        </p>
      </TextColumn>
      <TextColumn title="Ansvarlig bruk av kredittkort">
        <p>
          Et kredittkort kan være et nyttig betalingsmiddel, men det er viktig å ha kontroll på hvor
          mye du bruker og hva du skal betale tilbake. Prøv å holde forbruket innenfor det du vet at
          du kan betale når fakturaen kommer, og betal helst hele beløpet innen forfallsdato for å
          unngå renter. Det kan også være lurt å følge med på kredittgrensen og unngå å bruke
          kredittkortet som en langsiktig løsning på faste utgifter.
        </p>
        <p>
          For å få bedre oversikt over økonomien din kan du også sjekke gjelden din hos{' '}
          <Link to={routes.MY_SCOOPR} className={seoLinkClassName}>
            min Scoopr
          </Link>
          . Med god oversikt og regelmessig tilbakebetaling kan du dra nytte av fordelene ved
          kredittkort uten at kostnadene blir unødvendig høye.
        </p>
      </TextColumn>
    </TextColumns>
  );
};

const ChoosingSection = () => {
  return (
    <TextColumns>
      <TextColumn title="Hva bør du se etter når du velger kredittkort">
        <p>
          Når du velger kredittkort, bør du se etter et kort som passer til hvordan du faktisk skal
          bruke det. Sammenlign blant annet effektiv rente, årsgebyr, rentefri periode og andre
          gebyrer. Din{' '}
          <Link to={routes.CREDIT_CHECK} className={seoLinkClassName}>
            kredittscore
          </Link>{' '}
          eller samlede kredittvurdering kan også ha betydning for hvilke kredittkort og
          kredittgrenser du kan få.
        </p>
        <p>
          Det kan også være verdt å se på hvilke fordeler kortet tilbyr, som cashback, bonuspoeng,
          rabatter eller reiseforsikring. Reiser du mye, kan et kort med gode reisefordeler være
          aktuelt, mens cashback kan være mer relevant dersom kortet først og fremst skal brukes til
          daglige kjøp. Ved å sammenligne vilkår og fordeler kan du lettere finne et kredittkort som
          passer dine behov.
        </p>
      </TextColumn>
      <TextColumn title="Forskjellen mellom kredittkort og forbrukslån">
        <p>
          Kredittkort og{' '}
          <Link to={routes.CONSUMER_LOAN} className={seoLinkClassName}>
            forbrukslån
          </Link>{' '}
          er begge former for usikret kreditt, men de passer til ulike behov. Med{' '}
          <Link to={routes.CONSUMER_LOAN} className={seoLinkClassName}>
            forbrukslån
          </Link>{' '}
          får du vanligvis utbetalt et bestemt beløp som betales tilbake over en avtalt periode. Et
          kredittkort gir deg derimot en kredittramme du kan bruke ved behov, og du betaler bare
          tilbake det du har brukt.
        </p>
        <p>
          Kredittkort kan være praktisk til mindre kjøp og kortsiktige utgifter, mens forbrukslån
          ofte brukes til større, planlagte kostnader. Før du velger, bør du sammenligne effektiv
          rente, gebyrer og tilbakebetalingsvilkår.
        </p>
      </TextColumn>
    </TextColumns>
  );
};

const CreditCardSection = (props: { onApply: (cardName: string) => Promise<void> }) => {
  return (
    <Section.Container className="bg-white text-v1-background-200">
      <Section.Content>
        <h2 className="mb-8 text-center typography-v1-h2 text-balance">
          Sammenlign fordeler og vilkår
        </h2>

        <div className="mx-auto grid items-center gap-4 max-xl:max-w-4xl max-sm:max-w-md sm:grid-cols-2 xl:grid-cols-4">
          <CreditCard card={creditCards['InstaBank']} applyAction={props.onApply} />
          <CreditCard card={creditCards['TF Bank Nordic']} applyAction={props.onApply} />
          <CreditCard card={creditCards['Bank Norwegian']} applyAction={props.onApply} />
          <CreditCard card={creditCards['Morrow Bank']} applyAction={props.onApply} disabled />
        </div>
      </Section.Content>
    </Section.Container>
  );
};

const CreditCard = (props: {
  card: CreditCardInfo;
  applyAction: (cardName: string) => Promise<void>;
  disabled?: boolean;
}) => {
  const { card } = props;

  return (
    <article
      className={clsx(
        'flex h-full flex-col rounded-2xl bg-v1-neutral-20 p-4 shadow-sm',
        props.disabled && 'opacity-60',
      )}
    >
      <h3 className="mb-4 text-center typography-v1-h4">{card.name}</h3>

      <div className="mb-6 flex h-36 items-center justify-center">
        <img
          src={card.imageUrl}
          alt={`Kredittkort fra ${card.name}`}
          width={206}
          height={132}
          className="max-h-full max-w-full object-contain"
        />
      </div>

      <dl className="mb-6 flex flex-col gap-4 typography-v1-body4">
        <CardDetail
          label="Effektiv rente"
          value={`${percentFormatter.format(card.effectiveRate / 100)} %`}
        />
        <CardDetail
          label="Kredittgrense"
          value={`Opptil ${formatPrice(card.creditLimit / 100, 0)}`}
        />
        <CardDetail
          label="Rentefri periode"
          value={`${card.interestFreePeriod > 0 ? 'opptil' : ''} ${card.interestFreePeriod} dager`}
        />
        <CardDetail label="Årsavgift" value={formatPrice(card.annualFee / 100, 0)} />
      </dl>

      <CardFeature label="Forsikringer" features={card.insurances.map((label) => ({ label }))} />

      <div className="mb-6 typography-v1-body4">
        <h4 className="font-bold">Bonus</h4>
        <p>{card.bonus}</p>
      </div>

      <CardFeature label="Annet" features={card.other} />

      <div className="mt-auto flex flex-col gap-4 pt-2">
        <Button
          color="blue"
          className="w-full"
          action={() => props.applyAction(card.name)}
          disabled={props.disabled}
        >
          {props.disabled ? 'Kommer snart!' : 'Søk nå'}
        </Button>
        <p className="typography-v1-caption1">{card.representativeExample}</p>
      </div>
    </article>
  );
};

const CardDetail = (props: { label: string; value: string }) => {
  return (
    <div>
      <dt className="font-bold">{props.label}</dt>
      <dd>{props.value}</dd>
    </div>
  );
};

const CardFeature = (props: { label: string; features: CreditCardFeature[] }) => {
  return (
    <div className="mb-6 typography-v1-body4">
      <h4 className="mb-2 font-bold">{props.label}</h4>
      <ul className="flex flex-wrap gap-1">
        {props.features.map((feature) => (
          <li
            key={feature.label}
            className="inline-flex items-center gap-1 rounded-full border border-v1-neutral-40 px-2 py-1 typography-v1-caption1 [&_svg]:size-4"
          >
            {feature.label}
            {feature.Icon && <feature.Icon aria-hidden />}
          </li>
        ))}
      </ul>
    </div>
  );
};
